import { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "@/utils/env";

const LOGO = "https://storage.googleapis.com/connct-cba18.firebasestorage.app/frontend-assets/Conncct-logo.png";

// Pre-computed confetti burst for the success overlay (deterministic so it never
// re-randomises on re-render).
const CONFETTI = Array.from({ length: 16 }).map((_, i) => {
  const angle = (i / 16) * Math.PI * 2;
  const dist = 90 + (i % 4) * 30;
  return {
    dx: Math.round(Math.cos(angle) * dist),
    dy: Math.round(Math.sin(angle) * dist - 10),
    dr: (i % 2 ? 1 : -1) * (160 + i * 18),
    color: ["#2563EB", "#FACC15", "#16A34A", "#F87171", "#60A5FA"][i % 5],
    delay: (i % 5) * 45,
    size: 7 + (i % 3) * 3,
  };
});

function EmailVerification() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).email : "");
  const firstName = location.state?.firstName || (localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')!).firstName : "");

  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [error, setError] = useState("");
  const [verified, setVerified] = useState(false);
  // Stateless HMAC fallback token returned by /send-verification-code, so verification
  // succeeds even when the backend can't persist the code. Always keep the latest one.
  const [verifyToken, setVerifyToken] = useState<string>("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    handleResendCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(0, 1);
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newCode = [...code];
    for (let i = 0; i < pasteData.length && i < 6; i++) newCode[i] = pasteData[i] || '';
    setCode(newCode);
    if (pasteData.length === 6) inputRefs.current[5]?.focus();
    else if (pasteData.length > 0) inputRefs.current[Math.min(pasteData.length, 5)]?.focus();
  };

  const handleVerify = async () => {
    setError("");
    setLoading(true);

    const verificationCode = code.join('');
    if (verificationCode.length !== 6) {
      setError("Please enter all 6 digits");
      setLoading(false);
      return;
    }

    try {
      let uid: string | undefined;
      try { uid = JSON.parse(localStorage.getItem('user') || '{}').uid; } catch { /* ignore */ }
      const token = verifyToken || localStorage.getItem('verifyToken') || undefined;

      const response = await fetch(`${API_BASE_URL}/auth/verify-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code: verificationCode, uid, verifyToken: token })
      });

      if (!response.ok) {
        let errorMessage = 'Verification failed. Please try again.';
        try { const data = await response.json(); errorMessage = data.error || errorMessage; }
        catch (parseError) { console.error('Error parsing response:', parseError); }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Verification failed');

      const userStr = localStorage.getItem('user');
      const authTokensStr = localStorage.getItem('authTokens');
      if (!userStr || !authTokensStr) {
        console.error("❌ User data missing after verification!");
        setError("Session expired. Please sign up again.");
        setTimeout(() => navigate("/Msignup", { replace: true }), 2000);
        return;
      }

      try {
        const tokens = JSON.parse(authTokensStr);
        if (tokens && tokens.idToken) {
          localStorage.setItem('idToken', tokens.idToken);
          localStorage.setItem('token', tokens.idToken);
        }
        if (tokens.refreshToken) localStorage.setItem('refreshToken', tokens.refreshToken);

        const user = JSON.parse(userStr);
        if (data.uid) user.uid = data.uid;
        user.emailVerified = true;
        localStorage.setItem('user', JSON.stringify(user));

        const verifyResponse = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
          headers: { Authorization: `Bearer ${tokens.idToken}` }
        });
        if (!verifyResponse.ok) {
          setError("Verification completed but account setup failed. Please contact support.");
          return;
        }
      } catch (e) {
        console.error('Error processing auth data:', e);
        setError("Authentication error. Please try again or sign up again.");
        setTimeout(() => navigate("/Msignup", { replace: true }), 2000);
        return;
      }

      // Activate the user's ORIGIN product on the backend: accounting for "setup"
      // signups, consulting otherwise. The other product stays deactivated until the
      // user explicitly activates it. (Single source of truth — no localStorage guessing.)
      const signupUserType = localStorage.getItem('signupUserType');
      try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        const tk = JSON.parse(localStorage.getItem('authTokens') || '{}');
        if (u?.uid) {
          fetch(`${API_BASE_URL}/auth/user/${u.uid}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json', ...(tk?.idToken ? { Authorization: `Bearer ${tk.idToken}` } : {}) },
            body: JSON.stringify(signupUserType === 'setup' ? { accountingActivated: true } : { consultingActivated: true }),
          }).catch(() => {});
        }
      } catch { /* ignore */ }

      // Show the animated success overlay, then route based on the chosen service.
      setVerified(true);
      setTimeout(() => {
        if (signupUserType === 'setup') navigate("/setup-service", { replace: true });
        else navigate("/Countrysl", { replace: true });
      }, 1900);

    } catch (err: any) {
      console.error("Verification error:", err);
      if (err.message === 'Failed to fetch' || !navigator.onLine) {
        setError("Cannot connect to server. Please check your internet connection.");
      } else if (err.message.includes('code not found') || err.message.includes('not found')) {
        setError("Verification code not found. Please request a new code.");
      } else if (err.message.includes('expired')) {
        setError("Verification code has expired. Please request a new code.");
      } else if (err.message.includes('Invalid')) {
        setError("Incorrect verification code. Please check and try again.");
      } else {
        setError(err.message || "Verification failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) { setError("Email not found. Please sign up again."); return; }
    if (resending) return;

    setError("");
    setResendSuccess(false);
    setResending(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/send-verification-code`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, firstName })
      });
      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Failed to send verification code');

      if (data.verifyToken) {
        setVerifyToken(data.verifyToken);
        try { localStorage.setItem('verifyToken', data.verifyToken); } catch { /* ignore */ }
      }

      setResendSuccess(true);
      setCode(["", "", "", "", "", ""]);
      inputRefs.current[0]?.focus();
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err: any) {
      console.error("Resend error:", err);
      if (err.message === 'Failed to fetch') setError("Cannot connect to server. Please check your internet connection.");
      else setError(err.message || "Failed to send verification code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const codeComplete = code.join('').length === 6;
  const maskedEmail = email
    ? email.replace(/^(.)(.*)(@.*)$/, (_m: string, a: string, b: string, c: string) => a + '•'.repeat(Math.max(b.length, 1)) + c)
    : '';

  // Theme the page by the product the user is signing up for: consulting → blue,
  // accounting → brand gold/yellow.
  const isAccounting = (() => {
    try { return localStorage.getItem('signupUserType') === 'setup' || localStorage.getItem('activeServiceView') === 'accounting'; }
    catch { return false; }
  })();
  const theme: Record<string, string> = isAccounting
    ? {
        '--ev-bg': 'linear-gradient(160deg,#FFF8E3 0%,#FFFDF5 48%,#FCF3D9 100%)',
        '--ev-blob1': '#FFE08A', '--ev-blob2': '#FDE68A',
        '--ev-grad': 'linear-gradient(135deg,#FFD54F 0%,#E6B400 100%)',
        '--ev-accent': '#B45309', '--ev-accent-soft': 'rgba(230,180,0,.30)',
        '--ev-tint': '#FFFBEB', '--ev-btn-text': '#17263A', '--ev-shadow': 'rgba(230,180,0,.55)',
        '--ev-success-grad': 'linear-gradient(160deg,#E6B400 0%,#B8860B 45%,#17263A 100%)',
      }
    : {
        '--ev-bg': 'linear-gradient(160deg,#EAF2FB 0%,#F7FAFF 48%,#E7EFFC 100%)',
        '--ev-blob1': '#93C5FD', '--ev-blob2': '#BFDBFE',
        '--ev-grad': 'linear-gradient(135deg,#2563EB 0%,#1D4ED8 100%)',
        '--ev-accent': '#1D4ED8', '--ev-accent-soft': 'rgba(37,99,235,.16)',
        '--ev-tint': '#EFF6FF', '--ev-btn-text': '#ffffff', '--ev-shadow': 'rgba(37,99,235,.6)',
        '--ev-success-grad': 'linear-gradient(160deg,#2563EB 0%,#1D4ED8 45%,#17263A 100%)',
      };

  return (
    <div className="ev-root" style={theme as React.CSSProperties}>
      <style>{`
        .ev-root {
          min-height: 100vh; position: relative; overflow: hidden;
          display: flex; flex-direction: column; align-items: center; justify-content: center;
          padding: 24px;
          background: var(--ev-bg);
          font-family: 'Montserrat', sans-serif;
        }
        .ev-blob { position: absolute; border-radius: 9999px; filter: blur(60px); opacity: .5; pointer-events: none; }
        .ev-blob1 { width: 420px; height: 420px; top: -120px; right: -90px; background: radial-gradient(circle, var(--ev-blob1), transparent 70%); animation: evFloat 9s ease-in-out infinite; }
        .ev-blob2 { width: 360px; height: 360px; bottom: -130px; left: -100px; background: radial-gradient(circle, var(--ev-blob2), transparent 70%); animation: evFloat 11s ease-in-out infinite reverse; }
        .ev-logo { height: 30px; width: auto; margin-bottom: 22px; animation: evRise .6s both; }
        .ev-card {
          position: relative; z-index: 2; width: 100%; max-width: 440px;
          background: #fff; border-radius: 24px; padding: 36px 34px 30px;
          box-shadow: 0 30px 70px -28px rgba(23,38,58,.35), 0 8px 24px -16px rgba(23,38,58,.2);
          border: 1px solid rgba(255,255,255,.8);
          animation: evRise .6s .05s both;
        }
        .ev-icon {
          width: 64px; height: 64px; border-radius: 18px; margin: 0 auto 18px;
          display: flex; align-items: center; justify-content: center; position: relative;
          background: var(--ev-grad);
          box-shadow: 0 12px 24px -8px var(--ev-shadow);
          animation: evPop .6s .12s both;
        }
        .ev-icon::after {
          content: ''; position: absolute; inset: 0; border-radius: 18px;
          border: 2px solid var(--ev-accent); animation: evRing 2.2s ease-out infinite;
        }
        .ev-title { font-size: 22px; font-weight: 800; color: #17263A; text-align: center; margin-bottom: 6px; animation: evRise .6s .16s both; }
        .ev-sub { font-size: 13px; color: #64748B; text-align: center; line-height: 1.5; margin-bottom: 24px; animation: evRise .6s .2s both; }
        .ev-sub b { color: var(--ev-accent); font-weight: 700; }
        .ev-msg { font-size: 12.5px; font-weight: 600; text-align: center; border-radius: 12px; padding: 9px 12px; margin-bottom: 16px; }
        .ev-err { background: #FEF2F2; border: 1px solid #FECACA; color: #DC2626; }
        .ev-ok  { background: #ECFDF5; border: 1px solid #A7F3D0; color: #15803D; }
        .ev-digits { display: flex; gap: 10px; justify-content: center; margin-bottom: 22px; }
        .ev-digits.shake { animation: evShake .4s; }
        .ev-digit {
          width: 50px; height: 58px; text-align: center; font-size: 24px; font-weight: 700; color: #17263A;
          background: #F8FAFC; border: 2px solid #E2E8F0; border-radius: 14px; outline: none;
          transition: border-color .18s, box-shadow .18s, transform .18s, background .18s;
          animation: evDigitIn .45s both;
        }
        .ev-digit:focus { border-color: var(--ev-accent); background: #fff; box-shadow: 0 0 0 4px var(--ev-accent-soft); transform: translateY(-2px); }
        .ev-digit.filled { border-color: var(--ev-accent); background: var(--ev-tint); }
        .ev-btn-primary {
          width: 100%; height: 50px; border-radius: 14px; border: none; cursor: pointer;
          color: var(--ev-btn-text); font-size: 14px; font-weight: 800; font-family: inherit;
          background: var(--ev-grad);
          box-shadow: 0 12px 24px -10px var(--ev-shadow);
          transition: transform .15s, opacity .15s; display: flex; align-items: center; justify-content: center; gap: 8px;
        }
        .ev-btn-primary:hover:not(:disabled) { transform: translateY(-2px); }
        .ev-btn-primary:disabled { opacity: .55; cursor: not-allowed; }
        .ev-resend { display: block; margin: 16px auto 4px; background: none; border: none; cursor: pointer;
          font-family: inherit; font-size: 13px; font-weight: 600; color: #64748B; }
        .ev-resend b { color: var(--ev-accent); }
        .ev-resend:disabled { opacity: .5; cursor: not-allowed; }
        .ev-cancel { display: block; margin: 8px auto 0; background: none; border: none; cursor: pointer;
          font-family: inherit; font-size: 12.5px; font-weight: 600; color: #94A3B8; }
        .ev-spin { width: 16px; height: 16px; border: 2px solid currentColor; border-top-color: transparent; opacity: .8; border-radius: 9999px; animation: evSpin .7s linear infinite; }

        .ev-success {
          position: fixed; inset: 0; z-index: 60; display: flex; flex-direction: column;
          align-items: center; justify-content: center; gap: 6px;
          background: var(--ev-success-grad);
          animation: evFadeIn .35s both;
        }
        .ev-check-wrap { position: relative; width: 112px; height: 112px; margin-bottom: 14px; }
        .ev-check-circle {
          width: 112px; height: 112px; border-radius: 9999px; background: #fff;
          display: flex; align-items: center; justify-content: center;
          box-shadow: 0 20px 50px -12px rgba(0,0,0,.4); animation: evPop .5s .1s both;
        }
        .ev-check-circle svg path { stroke-dasharray: 48; stroke-dashoffset: 48; animation: evDraw .5s .5s forwards cubic-bezier(.65,0,.45,1); }
        .ev-success h2 { color: #fff; font-size: 26px; font-weight: 800; animation: evFadeUp .5s .55s both; }
        .ev-success p { color: rgba(255,255,255,.7); font-size: 14px; animation: evFadeUp .5s .7s both; }
        .ev-confetti { position: absolute; top: 50%; left: 50%; border-radius: 2px; opacity: 0; animation: evConfetti .9s .5s ease-out forwards; }

        @keyframes evRise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }
        @keyframes evFadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
        @keyframes evFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes evPop { 0% { opacity: 0; transform: scale(.5); } 60% { transform: scale(1.08); } 100% { opacity: 1; transform: scale(1); } }
        @keyframes evFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-26px); } }
        @keyframes evRing { 0% { transform: scale(1); opacity: .5; } 100% { transform: scale(1.7); opacity: 0; } }
        @keyframes evShake { 0%,100% { transform: translateX(0); } 20%,60% { transform: translateX(-7px); } 40%,80% { transform: translateX(7px); } }
        @keyframes evDigitIn { from { opacity: 0; transform: translateY(10px) scale(.92); } to { opacity: 1; transform: none; } }
        @keyframes evSpin { to { transform: rotate(360deg); } }
        @keyframes evDraw { to { stroke-dashoffset: 0; } }
        @keyframes evConfetti { 0% { opacity: 1; transform: translate(-50%,-50%) rotate(0); } 100% { opacity: 0; transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--dr)); } }
        @media (prefers-reduced-motion: reduce) { .ev-root *, .ev-success * { animation-duration: .01ms !important; } }
      `}</style>

      <div className="ev-blob ev-blob1" />
      <div className="ev-blob ev-blob2" />

      <img src={LOGO} alt="Conncct" className="ev-logo" loading="lazy" />

      <div className="ev-card">
        <div className="ev-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="14" rx="2.5" />
            <path d="m3 7 9 6 9-6" />
          </svg>
        </div>

        <h1 className="ev-title">Verify your account</h1>
        <p className="ev-sub">
          {firstName ? `Hi ${firstName}, enter` : 'Enter'} the 6-digit code we sent to{' '}
          <b>{maskedEmail || 'your email'}</b>
        </p>

        {error && <div className="ev-msg ev-err">{error}</div>}
        {resendSuccess && !error && <div className="ev-msg ev-ok">Code sent — check your inbox.</div>}

        <div className={`ev-digits${error ? ' shake' : ''}`}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              disabled={loading || verified}
              className={`ev-digit${digit ? ' filled' : ''}`}
              style={{ animationDelay: `${0.22 + index * 0.05}s` }}
            />
          ))}
        </div>

        <button className="ev-btn-primary" onClick={handleVerify} disabled={loading || verified || !codeComplete}>
          {loading ? <><span className="ev-spin" /> Verifying…</> : 'Verify & continue →'}
        </button>

        <button className="ev-resend" onClick={handleResendCode} disabled={loading || resending || verified}>
          {resending ? 'Sending…' : <>Didn't get it? <b>Resend code</b></>}
        </button>
        <button className="ev-cancel" onClick={() => navigate(-1)} disabled={verified}>Back</button>
      </div>

      {verified && (
        <div className="ev-success">
          <div className="ev-check-wrap">
            {CONFETTI.map((c, i) => (
              <span
                key={i}
                className="ev-confetti"
                style={{
                  width: c.size, height: c.size, background: c.color,
                  ['--dx' as any]: `${c.dx}px`, ['--dy' as any]: `${c.dy}px`, ['--dr' as any]: `${c.dr}deg`,
                  animationDelay: `${c.delay}ms`,
                }}
              />
            ))}
            <div className="ev-check-circle">
              <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
          </div>
          <h2>Verified!</h2>
          <p>Taking you to the next step…</p>
        </div>
      )}
    </div>
  );
}

export default EmailVerification;
